import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, Pencil, Star, X } from "lucide-react";
import { LikeBar, usePhotoLikes } from "@/components/PhotoLikes";
import { ReportButton } from "@/components/ReportButton";

const Caption = ({ text, editable, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(text || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setDraft(text || ""); setEditing(false); }, [text]);
  if (!text && !editable) return null;
  const save = async () => { setBusy(true); try { await onSave(draft.trim()); setEditing(false); } finally { setBusy(false); } };
  return (
    <div className="absolute inset-x-0 bottom-[4.75rem] flex justify-center px-4" onClick={(e) => e.stopPropagation()} data-testid="photo-caption">
      {editing ? (
        <form className="flex w-full max-w-md items-center gap-2" onSubmit={(e) => { e.preventDefault(); save(); }}>
          <input autoFocus maxLength={80} value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="e.g. Master suite, upper deck"
            className="field h-11 flex-1 border-white/20 bg-black/60 text-white" aria-label="Photo caption" data-testid="photo-caption-input"
            onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); setEditing(false); } if (e.key === "ArrowLeft" || e.key === "ArrowRight") e.stopPropagation(); }} />
          <button type="submit" disabled={busy} className="btn-primary h-11 px-4 text-sm" data-testid="photo-caption-save">Save</button>
        </form>
      ) : (
        <button type="button" disabled={!editable} onClick={() => setEditing(true)} className={`flex max-w-md items-center gap-2 rounded-full bg-black/60 px-4 py-2 text-sm text-white backdrop-blur ${editable ? "hover:bg-black/80" : ""}`} data-testid="photo-caption-text">
          {text ? <span className="truncate">{text}</span> : <span className="text-white/70">Add a caption</span>}{editable && <Pencil className="h-3.5 w-3.5 shrink-0 text-white/70" />}
        </button>
      )}
    </div>
  );
};

export const PhotoViewer = ({ photos, paths, index, onIndex, onClose, alt = "Photo", onSetCover, coverPath, captions, onCaption }) => {
  const touch = useRef(null);
  const { likes, toggle } = usePhotoLikes(paths);
  const total = photos.length;
  const go = (d) => onIndex((index + d + total) % total);

  useEffect(() => {
    const key = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") onIndex((index + 1) % total);
      else if (e.key === "ArrowLeft") onIndex((index - 1 + total) % total);
    };
    document.addEventListener("keydown", key);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", key); document.body.style.overflow = overflow; };
  }, [index, total, onIndex, onClose]);

  const onTouchEnd = (e) => {
    if (touch.current === null) return;
    const dx = e.changedTouches[0].clientX - touch.current;
    touch.current = null;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
  };

  const navBtn = "absolute top-1/2 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-md transition-colors hover:bg-white/20 sm:flex";
  return createPortal(
    <div role="dialog" aria-modal="true" aria-label={`${alt} viewer`} className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 animate-in fade-in"
      onClick={onClose} onTouchStart={(e) => { touch.current = e.touches[0].clientX; }} onTouchEnd={onTouchEnd} data-testid="photo-viewer">
      <img src={photos[index]} alt={`${alt} ${index + 1} of ${total}`} onClick={(e) => e.stopPropagation()}
        className="max-h-[78vh] max-w-[94vw] select-none rounded-lg object-contain" draggable={false} data-testid="photo-viewer-image" />
      <p className="absolute left-1/2 top-5 -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 text-sm text-white tabular-nums" data-testid="photo-viewer-counter">{index + 1} / {total}</p>
      <button type="button" aria-label="Close" onClick={onClose} className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20" data-testid="photo-viewer-close"><X className="h-5 w-5" /></button>
      {paths && <LikeBar info={likes[paths[index]]} onToggle={() => toggle(paths[index])} />}
      {paths && (captions || onCaption) && <Caption key={paths[index]} text={captions?.[paths[index]]} editable={!!onCaption} onSave={(t) => onCaption(paths[index], t)} />}
      {onSetCover && paths && (
        <button type="button" disabled={paths[index] === coverPath} onClick={(e) => { e.stopPropagation(); onSetCover(paths[index]); }}
          className="absolute bottom-6 right-4 flex h-11 items-center gap-2 rounded-full bg-white/10 px-4 text-sm text-white backdrop-blur-md transition-colors hover:bg-white/20 disabled:opacity-80 sm:right-6"
          data-testid="photo-viewer-set-cover">
          <Star className={`h-4 w-4 ${paths[index] === coverPath ? "fill-current text-primary" : ""}`} /> {paths[index] === coverPath ? "Current cover" : "Make this the cover"}
        </button>
      )}
      {paths && likes[paths[index]] && !likes[paths[index]].is_owner && (
        <div className="absolute right-4 top-4 mr-14 sm:right-6 sm:top-6" onClick={(e) => e.stopPropagation()}>
          <ReportButton kind="photo" target={paths[index]} className="h-11 rounded-full bg-white/10 px-4 text-white/80 hover:bg-white/20" testId="photo-viewer-report" />
        </div>
      )}
      {total > 1 && <>
        <button type="button" aria-label="Previous photo" onClick={(e) => { e.stopPropagation(); go(-1); }} className={`${navBtn} left-4`} data-testid="photo-viewer-prev"><ChevronLeft className="h-6 w-6" /></button>
        <button type="button" aria-label="Next photo" onClick={(e) => { e.stopPropagation(); go(1); }} className={`${navBtn} right-4`} data-testid="photo-viewer-next"><ChevronRight className="h-6 w-6" /></button>
      </>}
    </div>,
    document.body,
  );
};
